import React from 'react';
import { Core, Form, Layout, Localization, Typography } from 'connex-cds';
import styled from 'styled-components';
import { find } from 'lodash';
import roundedToCents from '../common/util/round-to-cents';
import { useSetPricing } from '../../../../../useSetPricing';
import { useCurrency } from '../../../../../useCurrency';
import style from './style';
import { useTicketContext } from '../../../../../TicketContext';

const { Container } = Layout;
const { Subtitle } = Typography;
const { Translate } = Localization;

const Styled = styled.div`
  ${style}
`;
const CMDSERIES = ['COMMAND_SERIES_V2', 'COMMAND_SERIES_V3'];

export const Summary = ({ show = false, products = [], orderPricing = [], pricing = {}, paymentAmount = 0 }) => {
  const { getCurrency } = useCurrency();
  const { setPricing } = useSetPricing();
  const { ticket } = useTicketContext();
  const { setFieldValue } = Form.useFormContext();

  const originatorSystemType = React.useMemo(() => ticket?.originatorSystemType ?? '', [ticket?.originatorSystemType]);

  const isPricingValid = React.useMemo(() => {
    if (CMDSERIES.includes(originatorSystemType) && ticket?.priceSummary) {
      return true;
    }

    const validFlag = Boolean(pricing?.taxRate?.valid);
    const hasTaxRates = Array.isArray(pricing?.taxRate?.taxRates) && pricing?.taxRate?.taxRates?.length > 0;

    return validFlag || hasTaxRates;
  }, [originatorSystemType, ticket?.priceSummary, pricing?.taxRate?.valid, pricing?.taxRate?.taxRates]);

  const isFirstLoad = React.useMemo(() => ticket?.loadNumber === 1, [ticket?.loadNumber]);

  const reduceSubtotal = React.useCallback(
    (acum, product) => {
      if (!product?.priceSummary || !product?.quantity?.value) {
        return roundedToCents(Number(acum));
      }

      const quantity = product?.quantity?.value ?? 0;
      const unitPrice = product?.priceSummary?.unitPrice ?? 0;
      const isNotDriverSellable = !Boolean(product?.item?.isDriverSellable);

      if (CMDSERIES.includes(originatorSystemType) && isNotDriverSellable) {
        const extendedPrice = product?.priceSummary?.extendedPrice ?? 0;
        return roundedToCents(Number(acum) + Number(extendedPrice));
      }

      const value = unitPrice * quantity;

      return roundedToCents(Number(acum) + Number(value));
    },
    [originatorSystemType]
  );

  const reduceTotal = React.useCallback(
    (acum, product) => {
      if (!product?.priceSummary || !product?.quantity?.value) {
        return roundedToCents(Number(acum));
      }

      let priceSummary = product?.priceSummary;

      // Match the authoritative line item by productRef, only tightening on
      // itemType/isPrimary when the product carries them. Driver-added products
      // omit those fields, so requiring them here would miss the match and fall
      // back to a stale (quantity-1) price.
      const contextProduct = find(ticket?.lineItems, lineItem => {
        if (lineItem?.item?.productRef !== product?.item?.productRef) {
          return false;
        }

        const itemTypeMatches = product?.item?.itemType == null || lineItem?.item?.itemType === product?.item?.itemType;
        const isPrimaryMatches = product?.isPrimary == null || lineItem?.isPrimary === product?.isPrimary;

        return itemTypeMatches && isPrimaryMatches;
      });

      if (contextProduct?.priceSummary) {
        priceSummary = contextProduct?.priceSummary;
      }

      let totalPrice = 0;

      if (isPricingValid) {
        totalPrice =
          product?.isTaxable || priceSummary?.isTaxable ? priceSummary?.totalPrice : priceSummary?.extendedPrice;
      }

      if (!isPricingValid) {
        const {
          priceSummary: { unitPrice },
          quantity: { value: quantity },
        } = product;

        totalPrice = unitPrice * quantity;
      }

      return roundedToCents(Number(acum) + Number(totalPrice));
    },
    [ticket?.lineItems, isPricingValid]
  );

  const productsFiltered = React.useMemo(() => {
    return products?.filter(product => !product?.driverDidNotAdd || !product?.quantity?.value);
  }, [products, pricing?.pricedLineItems]);

  const calculations = React.useMemo(() => {
    const subtotal = productsFiltered?.reduce(reduceSubtotal, 0);

    const total = productsFiltered?.reduce(reduceTotal, 0);

    const salesTax = roundedToCents(Math.abs(Number(total) - Number(subtotal)));

    const groupCostPriorTotal = roundedToCents(Number(ticket?.priceSummary?.accumulatedOrderGroupTotal || 0));

    const priorOrderCostTotal = roundedToCents(Number(groupCostPriorTotal) - Number(total));

    const priorOrderPaymentsTotal = roundedToCents(
      Number(ticket?.priceSummary?.accumulatedOrderGroupPriorPaymentsTotal || 0)
    );

    const orderShippedBalance = roundedToCents(Number(groupCostPriorTotal) - Number(priorOrderPaymentsTotal));

    const remainingBalance =
      groupCostPriorTotal > 0
        ? roundedToCents(Number(orderShippedBalance) - Number(paymentAmount))
        : roundedToCents(Number(total) - Number(paymentAmount));

    return {
      subtotal,
      total,
      salesTax,
      remainingBalance,
      groupCostPriorTotal,
      priorOrderCostTotal,
      priorOrderPaymentsTotal,
      orderShippedBalance,
    };
  }, [productsFiltered, paymentAmount, reduceSubtotal, reduceTotal, ticket?.priceSummary]);

  React.useEffect(() => {
    setFieldValue('deliveriesPriceSummary', {
      paymentAmount: paymentAmount ?? '',
    });
  }, [setFieldValue, calculations?.salesTax, calculations?.subtotal, calculations?.total, paymentAmount]);

  return (
    <Container flex={2} style={{ padding: '5px 10px' }}>
      <Styled>
        <Subtitle>
          <Translate stringId="costAndPaymentsSummary" data-testid="costAndPaymentsLabel" />
        </Subtitle>

        <div className="comment-section" style={{ padding: '5px' }}>
          <table width="100%">
            {!isFirstLoad && (
              <>
                {calculations?.priorOrderCostTotal > 0 && (
                  <tr>
                    <th className="left-header">
                      <Localization.Translate stringId="priorOrderCostTotal" data-testid="activity-header-text" />
                    </th>
                    <td>{getCurrency(calculations?.priorOrderCostTotal)}</td>
                  </tr>
                )}
                {calculations?.priorOrderPaymentsTotal > 0 && (
                  <tr>
                    <th className="left-header">
                      <Localization.Translate stringId="priorOrderPaymentTotal" data-testid="activity-header-text" />
                    </th>
                    <td>{getCurrency(calculations?.priorOrderPaymentsTotal)}</td>
                  </tr>
                )}
              </>
            )}
            <tr class="separator-row">
              <td colspan="2">
                <div class="line-separator"></div>
              </td>
            </tr>
            <tr>
              <th className="left-header">
                <Localization.Translate stringId="ticketSubtotal" data-testid="activity-header-text" />
              </th>
              <td>{getCurrency(calculations?.subtotal)}</td>
            </tr>
            <tr>
              <th className="left-header">
                <Localization.Translate stringId="ticketTax" data-testid="activity-header-text" />
                {!isPricingValid && <i className="icon warning-no-tax-icon-end fa-light fa-triangle-exclamation" />}
              </th>
              <td>{isPricingValid ? getCurrency(calculations?.salesTax) : '--'}</td>
            </tr>
            <tr>
              <th className="left-header">
                <Localization.Translate stringId="ticketTotal" data-testid="activity-header-text" />
              </th>
              <td>{getCurrency(calculations?.total)}</td>
            </tr>
            <tr class="separator-row">
              <td colspan="2">
                <div class="line-separator"></div>
              </td>
            </tr>
            {calculations?.groupCostPriorTotal > 0 && (
              <tr>
                <th className="left-header">
                  <Localization.Translate stringId="orderShippedCostTotal" data-testid="activity-header-text" />
                </th>
                <td>{getCurrency(calculations?.groupCostPriorTotal)}</td>
              </tr>
            )}
            {calculations?.orderShippedBalance > 0 && (
              <tr>
                <th className="left-header">
                  <Localization.Translate stringId="orderShippedBalance" data-testid="activity-header-text" />
                </th>
                <td>{getCurrency(calculations?.orderShippedBalance)}</td>
              </tr>
            )}

            {show && (
              <>
                <tr>
                  <th className="left-header">
                    <Localization.Translate stringId="paymentAmount" data-testid="activity-header-text" />
                  </th>
                  <td>{getCurrency(paymentAmount)}</td>
                </tr>
                <tr>
                  <th className="left-header">
                    <Localization.Translate stringId="remainingBalance" data-testid="activity-header-text" />
                  </th>
                  <td>{getCurrency(calculations?.remainingBalance)}</td>
                </tr>
              </>
            )}
          </table>
        </div>

        {!isPricingValid && (
          <>
            <div className="warning-no-tax">
              <i className="icon icon-start fa-thin fa-triangle-exclamation" />
              <span>
                <Localization.Translate
                  stringId="noTaxInformationIsAvailable"
                  data-testid="no-tax-information-is-available"
                />
              </span>
            </div>

            <Core.Button
              className="retry-pull-tax-information"
              stringId="retryPullTaxInformation"
              onClick={setPricing}
              type="primary"
              data-testid="retry-pull-tax-information"
            />
          </>
        )}
      </Styled>
    </Container>
  );
};
